import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "@apollo/client";
import { Box, CircularProgress, Alert } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { closeModal } from "../../../store";
import { GetPhysicianKitPrice } from "../../../apollo-client/api-query";
import BaseButton from "../../BaseButton";
import KitNumberStep from "./KitNumberStep";
import PhysicianOrderStep from "./PhysicianOrderStep";
// Note: MemberAgreementStep and InformedConsentStep have been moved to the patient portal
// These are now collected directly from the patient after they click the invite link
import CheckoutStep from "./CheckoutStep";
import ConfirmationStep from "./ConfirmationStep";

const STEPS = {
  KIT_NUMBER: 1,
  PHYSICIAN_ORDER: 2,
  CHECKOUT: 3,
  CONFIRMATION: 4
};

export default function AddPatientModal() {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(STEPS.KIT_NUMBER);

  // Fetch physician's kit price
  const { data: priceData, loading: priceLoading, error: priceError } = useQuery(GetPhysicianKitPrice, {
    fetchPolicy: 'network-only', // Always get fresh price
  });

  const [formData, setFormData] = useState({
    // Step 1: Kit Number
    kitCode: '',

    // Step 2: Physician Order
    patientFirstName: '',
    patientLastName: '',
    patientDateOfBirth: '',
    patientSex: '',
    patientPhone: '',
    physicianNotes: '',
    noBoneMarrowTransplant: false,
    noRecentBloodTransfusion: false,
    physicianName: '',
    physicianNpi: '',

    // Note: membershipAgreement and informedConsent are now collected
    // from the patient in the patient portal (after they click invite link)

    // Step 3: Checkout
    email: '',

    // Confirmation data (populated after successful invite)
    inviteUrl: '',
    emailSentTo: ''
  });

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  // Double-escape to close: track last escape press time
  const lastEscapeRef = useRef(0);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        const now = Date.now();
        if (now - lastEscapeRef.current < 500) {
          // Second escape within 500ms - close modal
          handleCloseModal();
        }
        lastEscapeRef.current = now;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNext = (stepData) => {
    setFormData(prev => ({ ...prev, ...stepData }));
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.KIT_NUMBER:
        return (
          <KitNumberStep
            data={formData}
            onNext={handleNext}
            onClose={handleCloseModal}
          />
        );
      case STEPS.PHYSICIAN_ORDER:
        return (
          <PhysicianOrderStep
            data={formData}
            onNext={handleNext}
            onBack={handleBack}
            onClose={handleCloseModal}
          />
        );
      case STEPS.CHECKOUT:
        return (
          <CheckoutStep
            data={formData}
            onNext={handleNext}
            onBack={handleBack}
            onClose={handleCloseModal}
            kitPriceCents={priceData?.physicianKitPrice?.priceCents}
            kitPriceDollars={priceData?.physicianKitPrice?.priceDollars}
            familyDiscountEnabled={priceData?.physicianKitPrice?.familyDiscountEnabled}
            familyDiscountPriceCents={priceData?.physicianKitPrice?.familyDiscountPriceCents}
            familyDiscountPriceDollars={priceData?.physicianKitPrice?.familyDiscountPriceDollars}
          />
        );
      case STEPS.CONFIRMATION:
        return (
          <ConfirmationStep
            data={formData}
            onClose={handleCloseModal}
            kitPriceDollars={priceData?.physicianKitPrice?.priceDollars}
            familyDiscountPriceDollars={priceData?.physicianKitPrice?.familyDiscountPriceDollars}
          />
        );
      default:
        return null;
    }
  };

  // Show loading while fetching price
  if (priceLoading) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', sm: '600px', md: '700px' },
          maxWidth: '90vw',
          borderRadius: '16px',
          background: 'white',
          padding: { xs: '24px 16px', sm: '40px 32px' },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error if price fetch failed
  if (priceError) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', sm: '600px', md: '700px' },
          maxWidth: '90vw',
          borderRadius: '16px',
          background: 'white',
          padding: { xs: '24px 16px', sm: '40px 32px' }
        }}
      >
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to load kit pricing. Please try again or contact support.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <BaseButton
            type="button"
            onClick={handleCloseModal}
            buttonType="primary"
            sx={{ minWidth: '140px' }}
          >
            Close
          </BaseButton>
        </Box>
      </Box>
    );
  }

  // Show error if no active price is configured for this physician
  const hasValidPrice = priceData?.physicianKitPrice &&
                        priceData.physicianKitPrice.priceCents != null &&
                        priceData.physicianKitPrice.priceDollars != null &&
                        priceData.physicianKitPrice.active === true;

  if (!hasValidPrice) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', sm: '600px', md: '700px' },
          maxWidth: '90vw',
          borderRadius: '16px',
          background: 'white',
          padding: { xs: '24px 16px', sm: '40px 32px' }
        }}
      >
        <Alert severity="warning" sx={{ mb: 3 }}>
          Sorry, Add Patient isn't available yet. No active kit price has been configured for your account. Please contact support to set up pricing.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <BaseButton
            type="button"
            onClick={handleCloseModal}
            buttonType="primary"
            sx={{ minWidth: '140px' }}
          >
            Close
          </BaseButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: '100%', sm: '600px', md: '700px' },
        maxWidth: '90vw',
        maxHeight: '90vh',
        borderRadius: '16px',
        background: 'white',
        padding: { xs: '24px 16px', sm: '40px 32px' },
        overflowY: 'auto'
      }}
    >
      {/* Progress indicator */}
      {currentStep < STEPS.CONFIRMATION && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            mb: 3
          }}
        >
          {[1, 2, 3].map((step) => (
            <Box
              key={step}
              sx={{
                width: '40px',
                height: '4px',
                borderRadius: '2px',
                background: step <= currentStep ? '#5B87EF' : '#ECEFF1'
              }}
            />
          ))}
        </Box>
      )}

      {/* Step content */}
      {renderStep()}

      {/* Close button */}
      <Box
        sx={{
          position: 'absolute',
          right: '22px',
          top: '22px',
          cursor: 'pointer',
          zIndex: 10
        }}
        onClick={handleCloseModal}
      >
        <CloseIcon sx={{ width: 32, height: 32, color: '#46545F' }} />
      </Box>
    </Box>
  );
}
